<template>
    <div
        :class="['horizontal-nav-group', { active: hasActiveReport, open: showDropdown }]"
        v-if="sidebarLinkGroupFiltered && sidebarLinkGroupFiltered.length"
    >
        <div class="nav-dropdown" @click="toggleDropdown" ref="dropdown">
            <div class="nav-header">
                <div class="nav-icon">
                    <i class="material-icons">{{ sidebarLinkGroup.icon }}</i>
                </div>
                <span class="nav-title">{{ sidebarLinkGroup.title }}</span>
                <div class="nav-count">{{ sidebarLinkGroupFiltered.length }}</div>
                <i class="material-icons dropdown-arrow" :class="{ rotated: showDropdown }">
                    expand_more
                </i>
            </div>
            
            <transition name="dropdown-slide">
                <div class="nav-dropdown-content" v-if="showDropdown">
                    <div class="dropdown-items">
                        <router-link
                            v-for="(sidebarLink, i) of sidebarLinkGroupFiltered"
                            :key="i"
                            :to="sidebarLink.link"
                            :class="['dropdown-item', { active: $route.path === sidebarLink.link }]"
                            @click.native="handleItemClick"
                            v-if="!sidebarLink.forceReload"
                        >
                            <span class="item-title">{{ sidebarLink.title }}</span>
                            <i class="material-icons item-arrow" v-if="$route.path === sidebarLink.link">
                                arrow_forward_ios
                            </i>
                        </router-link>
                        
                        <a
                            v-for="(sidebarLink, i) of sidebarLinkGroupFiltered.filter(l => l.forceReload)"
                            :key="`reload-${i}`"
                            :href="sidebarLink.link"
                            :class="['dropdown-item', { active: $route.path === sidebarLink.link }]"
                        >
                            <span class="item-title">{{ sidebarLink.title }}</span>
                            <i class="material-icons item-arrow" v-if="$route.path === sidebarLink.link">
                                arrow_forward_ios
                            </i>
                        </a>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission } from "../../../store/permission"
import { scrollPageToTop } from "../../../utils/scroll-container"

export default {
    props: {
        sidebarLinkGroup: { type: Object, default: () => ({}) },
    },
    data() {
        return { 
            showDropdown: false 
        }
    },
    mounted() {
        this.checkActiveReport()
        // Close dropdown when clicking outside
        document.addEventListener('click', this.handleOutsideClick)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick)
    },
    methods: {
        checkActiveReport() {
            // Don't auto-open on mount, just check if any report is active
        },
        handleOutsideClick(event) {
            if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
                this.showDropdown = false
            }
        },
        handleItemClick() {
            this.showDropdown = false
            scrollPageToTop()
        },
        toggleDropdown() {
            this.showDropdown = !this.showDropdown
        },
    },
    watch: {
        $route(e) {
            this.showDropdown = false
        },
    },
    computed: {
        ...mapState("auth", ["user"]),

        hasActiveReport() {
            return this.sidebarLinkGroupFiltered.some(link => this.$route.path === link.link)
        },

        sidebarLinkGroupFiltered() {
            // filter by permissions
            const filteredLinks = this.sidebarLinkGroup.data.filter(
                (sidebarLink) => {
                    const permissionIds = [sidebarLink.permission, ...(sidebarLink.requiredPermissions || [])]
                    const result = permissionIds.every(permissionId => checkHasPermission({
                        isAdmin: this.user.isAdmin,
                        permissionId,
                        userPermissionIds: this.user.permissionIds,
                    }))
                    return result
                }
            )
            return filteredLinks
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.horizontal-nav-group {
    position: relative;

    &.open {
        z-index: 3000;
    }
    
    &.active {
        .nav-header {
            background: linear-gradient(135deg, $primaryColor 0%, darken($primaryColor, 5%) 100%);
            color: white;
            
            .nav-icon i {
                color: $secondaryColor;
            }
            
            .nav-title {
                color: white;
            }
            
            .nav-count {
                background: rgba(255, 255, 255, 0.2);
                color: white;
            }
            
            .dropdown-arrow {
                color: white;
            }
        }
    }
}

.nav-dropdown {
    position: relative;
    z-index: 2000;
}

.nav-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    position: relative;
    z-index: 2001;
    
    &:hover {
        background: $lightHoverColor;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
}

.nav-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    
    i {
        font-size: 18px;
        color: $primaryColor;
    }
}

.nav-title {
    font-size: 14px;
    font-weight: 600;
    color: $textColor;
}

.nav-count {
    background: $lightBorderColor;
    color: $textColor;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    min-width: 24px;
    text-align: center;
}

.dropdown-arrow {
    font-size: 16px;
    color: rgba($textColor, 0.6);
    transition: transform 0.3s ease;
    margin-left: 4px;
    
    &.rotated {
        transform: rotate(180deg);
    }
}

.nav-dropdown-content {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 240px;
    width: max-content;
    max-width: 320px;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    z-index: 9999;
    margin-top: 4px;
    overflow: hidden;
}

.dropdown-items {
    padding: 8px;
    max-height: 300px;
    overflow-y: auto;
}

.dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    text-decoration: none;
    border-radius: 8px;
    margin-bottom: 2px;
    transition: all 0.2s ease;
    
    &:last-child {
        margin-bottom: 0;
    }
    
    &:hover {
        background: $lightHoverColor;
    }
    
    &.active {
        background: linear-gradient(135deg, $secondaryColor 0%, lighten($secondaryColor, 5%) 100%);
        color: white;
        
        .item-title {
            color: white;
            font-weight: 600;
        }
        
        .item-arrow {
            opacity: 1;
        }
    }
}

.item-title {
    font-size: 14px;
    color: $textColor;
    font-weight: 500;
}

.item-arrow {
    font-size: 12px;
    color: white;
    opacity: 0;
    transition: all 0.2s ease;
}

/* Animations */
.dropdown-slide-enter-active, .dropdown-slide-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-slide-enter {
    opacity: 0;
    transform: translateY(-8px);
}

.dropdown-slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* Responsive Design */
@media (max-width: 768px) {
    .nav-header {
        padding: 10px 12px;
        
        .nav-title {
            font-size: 13px;
        }
    }
    
    .nav-dropdown-content {
        position: fixed;
        top: auto;
        left: 16px;
        right: 16px;
        max-width: calc(100vw - 32px);
        z-index: 9999;
    }
    
    .dropdown-item {
        padding: 14px 16px;
        
        .item-title {
            font-size: 15px;
        }
    }
}

/* Dark theme */
.dark {
    .nav-header {
        background: $darkCardBg;
        border-color: $darkBorderColor;
        
        .nav-title {
            color: $darkTextColor;
        }
        
        .nav-icon i {
            color: $secondaryColor;
        }
        
        &:hover {
            background: lighten($darkCardBg, 5%);
        }
    }
    
    .nav-dropdown-content {
        background: $darkCardBg;
        border-color: $darkBorderColor;
    }
    
    .dropdown-item {
        .item-title {
            color: $darkTextColor;
        }
        
        &:hover {
            background: lighten($darkCardBg, 5%);
        }
    }
}
</style> 
